import {db} from '@/firebase'
import EventEmitter from 'events'
import { store } from '@/store'
import moment from 'moment'
import i18n from '@/i18n'

class AppointmentInvitationRepository extends EventEmitter {
    constructor () {
        super()

        this.collection = db.collection('appointmentInvitations')

        return this
    }

    find (id, onSuccess, onError) {
        this.collection
            .doc(id)
            .get()
            .then((doc) => {
                if (doc.exists) {
                    onSuccess(doc.data())
                } else {
                    onError({message: i18n.t('Errors.appointment_invitation_could_not_be_loaded')})
                }
            })
            .catch(onError)
    }

    allForPatient(patientId, onSuccess, onError) {
        let currentTime = Math.floor((moment().hour() * 60 + moment().minute()) / 15)

        this.collection
            .where('patientId', '==', patientId)
            .where('date', '>=', moment().format('YYYY-MM-DD'))
            .where('appointmentId', '==', null)
            .where('hasBeenAutoCancelled', '==', false)
            .where('hasBeenCancelled', '==', false)
            .where('hasBeenDeclined', '==', false)
            .get()
            .then((querySnapshot) => {
                let invitations = {}

                querySnapshot.forEach((doc) => {
                    let invitation = doc.data()

                    // Only get appointments for other days or later this day
                    if (invitation.date != moment().format('YYYY-MM-DD') || invitation.time >= currentTime) {
                        invitations[doc.id] = invitation
                    }
                });

                onSuccess(invitations)
            })
            .catch(onError)
    }

    declineByPatient(invitation, onSuccess, onError) {
        this.find(invitation.id, (invitation) => {
            if (invitation.appointmentId) {
                onError({message: i18n.t('Errors.appointment_invitation_already_accepted')})
                return false
            }

            if (invitation.hasBeenCancelled || invitation.hasBeenAutoCancelled) {
                onError({message: i18n.t('Errors.appointment_invitation_cancelled')})
                return false
            }

            if (invitation.hasBeenDeclined) {
                onError({message: i18n.t('Errors.appointment_invitation_declined')})
                return false
            }

            db.collection('appointmentInvitations')
              .doc(invitation.id)
              .update({
                hasBeenDeclined: true
              })
              .then(onSuccess())
        }, onError)
    }

    acceptByPatient(invitation, onSuccess, onError) {
        this.find(invitation.id, (invitation) => {
            if (invitation.appointmentId) {
                onError({message: i18n.t('Errors.appointment_invitation_already_accepted')})
                return false
            }

            if (invitation.hasBeenCancelled || invitation.hasBeenAutoCancelled) {
                onError({message: i18n.t('Errors.appointment_invitation_cancelled')})
                return false
            }

            if (invitation.hasBeenDeclined) {
                onError({message: i18n.t('Errors.appointment_invitation_declined')})
                return false
            }

            let batch = db.batch()
            let newAppointment = db.collection('appointments').doc()

            let appointment = {
                appointmentInvitationId: invitation.id,
                id: newAppointment.id,
                emergencyId: invitation.emergencyId,
                emergencyType: invitation.emergencyType,
                emergencyPictures: invitation.emergencyPictures,
                patientId: invitation.patientId,
                patientFirstName: invitation.patientFirstName,
                patientLastName: invitation.patientLastName,
                patientAge: invitation.patientAge,
                patientEmail: invitation.patientEmail,
                patientPhone: invitation.patientPhone,
                timeSlotId: invitation.timeSlotId,
                date: invitation.date,
                time: invitation.time,
                doctorId: invitation.doctorId,
                doctorFirstName: invitation.doctorFirstName,
                doctorLastName: invitation.doctorLastName,
                doctorAvatar: invitation.doctorAvatar,
                isDoctorRegulated: invitation.isDoctorRegulated,
                officeId: invitation.officeId,
                officeName: invitation.officeName,
                officePhone: invitation.officePhone,
                officeStreetName: invitation.officeStreetName,
                officeStreetNumber: invitation.officeStreetNumber,
                officeZipCode: invitation.officeZipCode,
                officeLocality: invitation.officeLocality,
                officeCountry: invitation.officeCountry,
                officeLocation: invitation.officeLocation
            }

            batch
                // 1. Create Appointment
                .set(newAppointment, appointment)
                // 2. Update Emergency (set a reference to the Appointment and AppointmentInvitation)
                .update(db.collection('emergencies').doc(invitation.emergencyId), {
                    appointmentId: appointment.id,
                    appointmentInvitationId: invitation.id
                })
                // 3. Update TimeSlot (set a reference to the Appointment and remove reference to AppointmentInvitation)
                .update(db.collection('timeSlots').doc(invitation.timeSlotId), {
                    appointmentId: appointment.id
                })
                // 4. Update AppointmentInvitation (set a reference to the Appointment)
                .update(db.collection('appointmentInvitations').doc(invitation.id), {
                    appointmentId: appointment.id
                })
                .commit()
                .then(onSuccess(appointment.id))
        }, onError)
    }

    allForOfficeDate(doctorId, officeId, date, onSuccess, onError) {
        this.collection
            .where('doctorId', '==', doctorId)
            .where('officeId', '==', officeId)
            .where('date', '==', date)
            .get()
            .then((querySnapshot) => {
                let appointmentInvitations = {}

                querySnapshot.forEach((doc) => {
                    appointmentInvitations[doc.id] = doc.data()
                });

                onSuccess(appointmentInvitations)
            })
            .catch(onError)
    }

    cancel(appointmentInvitation, onSuccess, onError) {
        var batch = db.batch()

        var appointmentInvitationRef = this.collection.doc(appointmentInvitation.id)
        var emergencyRef = db.collection('emergencies').doc(appointmentInvitation.emergencyId)
        var timeSlotRef = db.collection('timeSlots').doc(appointmentInvitation.timeSlotId)

        var emergency
        var timeSlot

        appointmentInvitationRef.get().then((doc) => {
            appointmentInvitation = doc.data()

            appointmentInvitation.hasBeenCancelled = true

            emergencyRef.get().then((doc) => {
                emergency = doc.data()

                emergency.appointmentInvitationId = null

                timeSlotRef.get().then((doc) => {
                    timeSlot = doc.data()

                    timeSlot.appointmentInvitationId = null

                    batch.set(appointmentInvitationRef, appointmentInvitation)
                    batch.set(emergencyRef, emergency)
                    batch.set(timeSlotRef, timeSlot)

                    batch.commit()
                        .then(onSuccess)
                        .catch(onError);
                }).catch(onError)
            }).catch(onError)
        }).catch(onError)
    }

    accept(emergency, availability, doctor, office, onSuccess, onError) {
        var batch = db.batch()

        var appointmentInvitationRef = this.collection.doc()

        // Create the appointmentInvitationRef
        batch.set(appointmentInvitationRef, {
            appointmentId: null,
            id: appointmentInvitationRef.id,
            dateTimeCreated: new Date,
            hasBeenDeclined: false,
            hasBeenCancelled: false,
            hasBeenAutoCancelled: false,
            timeSlotId: availability.id,
            time: availability.time,
            date: availability.date,
            doctorId: doctor.id,
            doctorFirstName: doctor.firstName,
            doctorLastName: doctor.lastName,
            doctorAvatar: doctor.avatar,
            isDoctorRegulated: doctor.isRegulated,
            officeId: office.id,
            officeName: office.name,
            officeStreetName: office.streetName,
            officeStreetNumber: office.streetNumber,
            officeZipCode: office.zipCode,
            officeLocality: office.locality,
            officeCountry: office.countryId,
            officeLocation: office.location,
            officePhone: office.phone,
            emergencyId: emergency.id,
            emergencyType: emergency.type,
            emergencyPictures: emergency.pictures,
            patientId: emergency.patientId,
            patientAge: emergency.patientAge,
            patientFirstName: emergency.patientFirstName,
            patientLastName: emergency.patientLastName,
            patientEmail: emergency.patientEmail,
            patientPhone: emergency.patientPhone
        })

        batch.update(db.collection('emergencies').doc(emergency.id), {
            appointmentInvitationId: appointmentInvitationRef.id
        })

        batch.update(db.collection('timeSlots').doc(availability.id), {
            appointmentInvitationId: appointmentInvitationRef.id
        })

        batch.commit()
            .then(onSuccess)
            .catch(onError);
    }
}

export default new AppointmentInvitationRepository()


// WEBPACK FOOTER //
// ./src/data/AppointmentInvitationRepository.js